<template>
  <div class="planning-list planning">
    <Header
        :authVars="authVars"
        :loading="loading"
        title="Plannings"
        @go-home="homePage"
    >
      <!-- Pas de meta ici, on laisse seulement les actions -->
      <button class="btn btn-primary btn-sm" @click="showCreateForm = true">+ Nouveau</button>

      <LogoutButton :userAccountObjId="authVars.userAccountObjId"/>
    </Header>

    <div v-if="error" class="error alert alert-danger">
      {{ error }}
      <button class="close" @click="clearError">&times;</button>
    </div>

    <!-- Calendrier mensuel -->
    <MonthlyCalendar
        :day-headers="CALENDAR_CONFIG.DAY_HEADERS"
        :has-content-on-date="hasPlanningOnDate"
        :is-date-selected="isSelected"
        :loading="loadingNavigation"
        :months="monthsToDisplay"
        @next="goNext"
        @previous="goPrevious"
        @day-click="handleDayClick"
    />

    <div v-if="plannings.length === 0 && !loading" class="empty-state">
      <p>Aucun planning disponible.</p>
    </div>

    <!-- Tableau des plannings -->
    <div v-else class="planning-table">
      <table class="table">
        <thead>
        <tr>
          <th style="width: 15%">Jour</th>
          <th style="width: 30%">
            <table style="width: 100%; border: none;">
              <tbody>
              <tr>
                <td style="border: none; padding: 0; font-weight: bold;">Heure début</td>
                <td style="border: none; padding: 0; font-weight: bold;">Heure fin</td>
              </tr>
              </tbody>
            </table>
          </th>
          <th style="width: 15%">Créneaux</th>
          <th style="width: 20%">Centres</th>
          <th>Par</th>
          <th>Créé par</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr
            v-for="(planning, index) in filteredPlannings"
            :key="planning.id"
            class="clickable-row"
            @click="viewMeetings(planning.id)"
        >
          <td :class="{ 'no-border': isSameDateAsNext(index) }">
            <span v-if="index > 0 && formatDate(planning.startAt) === formatDate(filteredPlannings[index - 1].startAt)"></span>
            <span v-else>{{ formatDate(planning.startAt) }}</span>
          </td>
          <td>
            <table style="width: 100%; border: none;">
              <tbody>
              <tr>
                <td style="border: none; padding: 2px;">{{ formatTime(planning.startAt) }}</td>
                <td style="border: none; padding: 2px;">{{ formatTime(planning.endAt) }}</td>
              </tr>
              <tr>
                <td colspan="2" style="border: none; padding: 2px; font-size: 0.85rem; color: #666;">

                  <div class="planning-stats-badges">
                    <template v-for="(group, gi) in getPlanningBadges(planning.meetingList)" :key="group.key">
                      <hr v-if="gi > 0 && group.badges.length > 0" class="badge-group-sep">
                      <div v-if="group.badges.length > 0" class="badge-group">
                        <div
                            v-for="stat in group.badges"
                            :key="stat.key"
                            class="badge"
                            :class="stat.variant"
                        >
                          <svg v-if="stat.svgPath" :viewBox="stat.svgViewBox" class="badge-svg-icon" aria-hidden="true"><path :d="stat.svgPath"/></svg>
                          <span v-else>{{ stat.icon }}</span>
                          {{ stat.label }}
                        </div>
                      </div>
                    </template>
                  </div>

                </td>
              </tr>
              </tbody>
            </table>
          </td>
          <td>
            <div class="meeting-stats">
              <strong>
                {{ planning.meetingList.length }}
                {{ planning.grouped ? planning.meetingList.length > 1 ? 'places' : 'place' : planning.meetingList.length > 1 ? 'créneaux' : 'créneau' }}
              </strong>
            </div>


            <div v-if="planning.ofCandidatureMeeting" style="font-size: 0.75rem; color: #555; line-height: 1.38; margin-top: 6px;">
              <strong style="color: #333;font-size: 0.65rem;">{{ planning.ofCandidatureMeeting.ofName }}</strong>
              <address style="font-style: normal; margin-top: 4px;">
                <div style="display: flex; gap: 8px; align-items: baseline;">
                  <span style="font-size: 1.1em; color: #e63946; flex-shrink: 0;">📍</span>
                  <div>
                    {{ planning.ofCandidatureMeeting.campusAddress }}<br>
                    <span style="color: #666;">{{ planning.ofCandidatureMeeting.campusPostalCode }} {{ planning.ofCandidatureMeeting.campusCityName }}</span>
                  </div>
                </div>
              </address>
            </div>
          </td>
          <td style="font-size: 0.75rem; color: #666; line-height: 1.2;">
            <span v-if="planning.ofCandidaturesEffective && planning.ofCandidaturesEffective.length > 0">
              {{ planning.ofCandidaturesEffective.map(of => of.ofName).join(', ') }}
            </span>
            <span v-else>—</span>
          </td>
          <td>
            <p class="cursor-pointer" style="display: inline-block;" @click.stop="editPlanning(planning)">
              <span class="underline" style="color: var(--primary);">
            {{ formatPersonDisplayName(planning.interviewManager) }}
              </span>
              <span style="margin-left: 5px;">✎</span>
            </p>
          </td>
          <td>
            <em class="opacity-70" style="font-size: smaller;">
            {{ formatPersonDisplayName(planning.owner) }}
            </em>
          </td>
          <td class="actions" style="text-align: right;">
            <p class="cursor-pointer" style="display: inline-block;" @click.stop="deletePlanning(planning.id)">
              <span style="margin-left: 5px;">🗑️</span>
            </p>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <PlanningCreate
        v-if="showCreateForm"
        :of-obj-id="ofObjId"
        :user-account-obj-id="userAccountObjId"
        @close="handlePlanningClosed"
        @created="handlePlanningCreated"
    />
    <PlanningEdit
        v-if="selectedPlanning"
        :planning="selectedPlanning"
        @close="handlePlanningClosed"
        @updated="handlePlanningUpdated"
    />
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import {useRouter} from 'vue-router'
import {usePlanningStore} from '@/store/planningStore'
import {storeToRefs} from 'pinia'
import {useDateFormatter} from '@/composables/useDateFormatter'
import {formatPersonDisplayName} from '@/composables/useSearchAutocomplete'
import {CALENDAR_CONFIG, PLANNING_STATUS, PLANNING_STATUS_LABELS} from '@/constants/planning'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import PlanningCreate from './PlanningCreate.vue'
import PlanningEdit from './PlanningEdit.vue'
import MonthlyCalendar from '@/components/UI/MonthlyCalendar.vue'
import LogoutButton from './LogoutButton.vue'
import Header from './Header.vue'
import RouteEnum from "@/router/RouteEnum";

dayjs.locale('fr')

const props = defineProps({ofObjId: Number, userAccountObjId: Number})

const router = useRouter()
const planningStore = usePlanningStore()
const {plannings, authVars, loading, error} = storeToRefs(planningStore)
const {formatDate, formatTime, formatDateString} = useDateFormatter()

const showCreateForm = ref(false)
const selectedDate = ref(null)
const selectedPlanning = ref(null)
const loadingNavigation = ref(false)

const currentStartMonth = ref(dayjs().startOf('month'))

const monthsToDisplay = computed(() => {
  const months = []
  for (let i = 0; i < 3; i++) {
    const m = currentStartMonth.value.add(i, 'month')
    months.push({
      key: m.format('YYYY-MM'),
      name: m.format('MMMM YYYY'),
      year: m.year(),
      monthIndex: m.month(),
      daysInMonth: m.daysInMonth(),
      firstDayOfWeek: (m.startOf('month').day() + 6) % 7
    })
  }
  return months
})

const loadPlannings = async () => {
  const startDate = currentStartMonth.value.startOf('month').format('YYYY-MM-DD')
  const endDate = currentStartMonth.value.add(2, 'month').endOf('month').format('YYYY-MM-DD')
  try {
    await planningStore.fetchPlannings(props.ofObjId, props.userAccountObjId, startDate, endDate)
  } catch (e) {
    // L'erreur est déjà gérée dans le store via planningStore.error
  }
}

const goPrevious = async () => {
  selectedDate.value = null;
  loadingNavigation.value = true
  currentStartMonth.value = currentStartMonth.value.subtract(1, 'month')
  await loadPlannings()
  loadingNavigation.value = false
}

const goNext = async () => {
  selectedDate.value = null;
  loadingNavigation.value = true
  currentStartMonth.value = currentStartMonth.value.add(1, 'month')
  await loadPlannings()
  loadingNavigation.value = false
}


const homePage = () => router.push({
  name: RouteEnum.PLANNING_VIEW,
  params: {
    userAccountObjId: props.userAccountObjId, ofObjId: props.ofObjId
  }
})
const viewMeetings = (planningId) => router.push({name: RouteEnum.MEETING_LIST, params: {ofObjId: props.ofObjId, userAccountObjId: props.userAccountObjId, planningId}})
const editPlanning = (planning) => selectedPlanning.value = {...planning}
const handlePlanningUpdated = () => {
  selectedPlanning.value = null;
  loadPlannings()
}
const deletePlanning = (planningId) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer ce planning ?')) {
    planningStore.deletePlanning(props.ofObjId, props.userAccountObjId, planningId)
  }
}
const handlePlanningCreated = () => {
  showCreateForm.value = false;
  loadPlannings()
}

const handlePlanningClosed = () => {
  showCreateForm.value = false;
  selectedPlanning.value = null
  error.value = null
}

const clearError = () => planningStore.clearError()

const hasPlanningOnDate = (y, m, d) => {
  const dateStr = formatDateString(y, m, d)
  return plannings.value.some(p => new Date(p.startAt).toISOString().split('T')[0] === dateStr)
}

const isSelected = (y, m, d) => selectedDate.value === formatDateString(y, m, d)

const handleDayClick = ({year, monthIndex, day}) => {
  toggleDateFilter(year, monthIndex, day)
}

const toggleDateFilter = (y, m, d) => {
  if (!hasPlanningOnDate(y, m, d)) return
  const dateStr = formatDateString(y, m, d)
  selectedDate.value = (selectedDate.value === dateStr) ? null : dateStr
}

const filteredPlannings = computed(() => {
  if (!selectedDate.value) return plannings.value
  return plannings.value.filter(p => new Date(p.startAt).toISOString().split('T')[0] === selectedDate.value)
})

const isSameDateAsNext = (index) => {
  if (index < filteredPlannings.value.length - 1) {
    return formatDate(filteredPlannings.value[index].startAt) ===
        formatDate(filteredPlannings.value[index + 1].startAt)
  }
  return false
}


// ────────────────────────────────────────────────
// Statuts avec icônes et couleurs (2025/2026 style)
// ────────────────────────────────────────────────
const getPlanningBadges = (meetings) => {
  if (!meetings?.length) {
    return [
      { key: 'visibility', badges: [] },
      { key: 'mode', badges: [] },
      { key: 'status', badges: [{ key: 'empty', label: 'Aucun créneau', variant: 'badge-secondary', icon: '⋅⋅⋅' }] }
    ]
  }

  // Groupe 1 : visibilité
  const publicCount = meetings.filter(m => Boolean(m?.public)).length
  const privateCount = meetings.length - publicCount
  const visibilityBadges = []
  if (publicCount > 0) visibilityBadges.push({ key: 'public', label: `${publicCount} public${publicCount > 1 ? 's' : ''}`, variant: 'badge-public', icon: '🌐' })
  if (privateCount > 0) visibilityBadges.push({ key: 'private', label: `${privateCount} privé${privateCount > 1 ? 's' : ''}`, variant: 'badge-private', icon: '🔒' })

  // Groupe 2 : présentiel / distanciel
  const distancielCount = meetings.filter(m => !!m.meetLink).length
  const presentielCount = meetings.length - distancielCount
  const modeBadges = []
  if (presentielCount > 0) modeBadges.push({ key: 'presentiel', label: `${presentielCount} présentiel${presentielCount > 1 ? 's' : ''}`, variant: 'badge-presentiel', icon: '📍' })
  if (distancielCount > 0) modeBadges.push({ key: 'distanciel', label: `${distancielCount} distanciel${distancielCount > 1 ? 's' : ''}`, variant: 'badge-distanciel', svgPath: 'M0 5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1.5l3.553-2.22A.5.5 0 0 1 16 4.704v6.592a.5.5 0 0 1-.447.496L12 9.573V11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2z', svgViewBox: '0 0 16 16' })

  // Groupe 3 : statuts
  const stats = {}
  meetings.forEach(m => {
    const s = m.statut
    if (s !== undefined) stats[s] = (stats[s] || 0) + 1
  })

  const order = [
    PLANNING_STATUS.AVAILABLE,
    PLANNING_STATUS.PROPOSED,
    PLANNING_STATUS.WAITING,
    PLANNING_STATUS.ACCEPTED,
    PLANNING_STATUS.REFUSED,
    PLANNING_STATUS.COMPLETED,
    PLANNING_STATUS.CANCELLED
  ]

  const statusBadges = []
  order.forEach(status => {
    const count = stats[status] || 0
    if (count === 0) return

    let icon = '?'
    let variant = 'badge-secondary'
    let shortLabel = PLANNING_STATUS_LABELS[status] || 'Inconnu'

    switch (status) {
      case PLANNING_STATUS.AVAILABLE:
        icon = '✓'; variant = 'badge-success'; shortLabel = count > 1 ? 'disponibles' : 'disponible'; break
      case PLANNING_STATUS.PROPOSED:
        icon = '📩'; variant = 'badge-warning'; shortLabel = 'à envoyer'; break
      case PLANNING_STATUS.WAITING:
        icon = '✉️'; variant = 'badge-info'; shortLabel = 'envoyé' + (count > 1 ? 's' : ''); break
      case PLANNING_STATUS.ACCEPTED:
        icon = '✔'; variant = 'badge-primary'; shortLabel = 'accepté' + (count > 1 ? 's' : ''); break
      case PLANNING_STATUS.REFUSED:
        icon = '✘'; variant = 'badge-danger'; shortLabel = 'refusé' + (count > 1 ? 's' : ''); break
      case PLANNING_STATUS.COMPLETED:
        icon = '🏁'; variant = 'badge-dark'; shortLabel = 'terminé' + (count > 1 ? 's' : ''); break
      case PLANNING_STATUS.CANCELLED:
        icon = '🗑'; variant = 'badge-cancel'; shortLabel = 'annulé' + (count > 1 ? 's' : ''); break
    }

    statusBadges.push({ key: `stat-${status}`, label: `${count} ${shortLabel}`, variant, icon })
  })

  return [
    { key: 'visibility', badges: visibilityBadges },
    { key: 'mode', badges: modeBadges },
    { key: 'status', badges: statusBadges }
  ]
}

onMounted(async () => {
  await loadPlannings()
})


</script>

<style scoped>
.planning-stats-badges {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9rem;
}

.badge-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.badge-group-sep {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 0;
  width: 100%;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.3;
  font-size: 0.78rem;
}

/* Variantes de couleur */
.badge-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.badge-warning {
  background: #fff3e0;
  color: #e65100;
}

.badge-info {
  background: #e3f2fd;
  color: #1565c0;
}

.badge-primary {
  background: #e8f0fe;
  color: #1e40af;
}

.badge-danger {
  background: #ffebee;
  color: #c62828;
}

.badge-dark {
  background: #f5f5f5;
  color: #424242;
}

.badge-cancel {
  background: #fce4ec;
  color: #880e4f;
}

.badge-secondary {
  background: #f5f5f5;
  color: #616161;
  border: 1px dashed #aaa;
}

.badge-public {
  background: #ccfbf1;
  color: #115e59;
}

.badge-private {
  background: #ffedd5;
  color: #9a3412;
}

.badge-presentiel {
  background: #fce7f3;
  color: #9d174d;
}

.badge-distanciel {
  background: #ede9fe;
  color: #5b21b6;
}

.badge-svg-icon {
  width: 13px;
  height: 13px;
  fill: currentColor;
  flex-shrink: 0;
}
</style>
